import { useEffect, useMemo, useState } from 'react'
import { BatchPreviewCoverage, BatchPreviewMissingField } from './hooks/useViewThruBatchUpload/viewThruBatchUploadUtils'
import styles from './styles.module.scss'

const formatCount = (value: number) => value.toLocaleString('en-GB')

const formatPercent = (value: number, total: number) => {
    if (total <= 0) return '0%'
    const percent = Math.round((value / total) * 1000) / 10
    return Number.isInteger(percent) ? `${percent}%` : `${percent.toFixed(1)}%`
}

const formatCountWithPercent = (value: number, total: number) =>
    `${formatCount(value)} (${formatPercent(value, total)})`

type ConfirmTab = 'all' | string

const OverviewPanel = ({ coverage }: { coverage: BatchPreviewCoverage }) => (
    <div className={styles.confirmStats}>
        <div className={styles.confirmStat}>
            <span className={styles.confirmStatLabel}>
                Valid for Equifax
                <br />
                <span className={styles.confirmHint}>Has the required identity fields</span>
            </span>
            <span className={styles.confirmStatValue}>
                {formatCountWithPercent(coverage.equifaxValid, coverage.total)}
            </span>
        </div>
        <div className={`${styles.confirmStat} ${styles.confirmStatWarn}`}>
            <span className={styles.confirmStatLabel}>
                Valid for TransUnion
                <br />
                <span className={styles.confirmHint}>Same rows that also have Title</span>
            </span>
            <span className={styles.confirmStatValue}>
                {formatCountWithPercent(coverage.transUnionValid, coverage.total)}
            </span>
        </div>
        {coverage.missingTitle > 0 && (
            <div className={styles.confirmCallout} role="status">
                <span className={styles.confirmCalloutCount}>{formatCount(coverage.missingTitle)}</span>
                <span className={styles.confirmCalloutText}>
                    won&apos;t get TransUnion — missing Title (
                    {formatPercent(coverage.missingTitle, coverage.total)})
                </span>
            </div>
        )}
        {coverage.invalid > 0 && (
            <p className={styles.confirmHint}>
                {formatCountWithPercent(coverage.invalid, coverage.total)} other row
                {coverage.invalid === 1 ? '' : 's'} fail the identity check and will be skipped.
            </p>
        )}
    </div>
)

const MissingFieldPanel = ({
    field,
    total,
}: {
    field: BatchPreviewMissingField
    total: number
}) => (
    <div className={styles.confirmStats}>
        <div
            className={`${styles.confirmCallout} ${
                field.effect === 'skipped' ? styles.confirmCalloutSkipped : ''
            }`}
            role="status"
        >
            <span className={styles.confirmCalloutCount}>{formatCount(field.count)}</span>
            <span className={styles.confirmCalloutText}>
                {field.summary} ({formatPercent(field.count, total)})
            </span>
        </div>
        <p className={styles.confirmHint}>
            {field.effect === 'transunion'
                ? 'These rows still have the other identity fields, so Equifax can return results. TransUnion rejects a blank Title.'
                : 'These rows fail the identity check and will be skipped when the batch runs.'}
        </p>
    </div>
)

export const ConfirmCoverageBody = ({ coverage }: { coverage: BatchPreviewCoverage }) => {
    const missingFields = coverage.missingFields ?? []
    const tabs = useMemo(
        () => [
            { id: 'all' as ConfirmTab, label: 'All' },
            ...missingFields.map((field) => ({
                id: field.key as ConfirmTab,
                label: `Missing ${field.label}`,
                count: field.count,
            })),
        ],
        [missingFields]
    )
    const [tab, setTab] = useState<ConfirmTab>('all')

    useEffect(() => {
        setTab('all')
    }, [coverage.total, coverage.missingTitle, coverage.invalid])

    const activeField = missingFields.find((field) => field.key === tab) || null

    return (
        <div className={styles.confirmCoverage}>
            <div className={styles.confirmTabs} role="tablist" aria-label="Validation results">
                {tabs.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === item.id}
                        className={`${styles.confirmTab} ${tab === item.id ? styles.confirmTabActive : ''}`}
                        onClick={(event) => {
                            event.stopPropagation()
                            setTab(item.id)
                        }}
                    >
                        {item.label}
                        {'count' in item && item.count != null ? (
                            <span className={styles.confirmTabCount}>{formatCount(item.count)}</span>
                        ) : null}
                        {tab === item.id ? <span className={styles.confirmTabIndicator} aria-hidden /> : null}
                    </button>
                ))}
            </div>
            <div className={styles.confirmPanel}>
                {tab === 'all' || !activeField ? (
                    <OverviewPanel coverage={coverage} />
                ) : (
                    <MissingFieldPanel field={activeField} total={coverage.total} />
                )}
            </div>
        </div>
    )
}
